import { useSerialize } from "@lxc/app-device-common/src/composables/useSerialize";
import { computed, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import type {
  FilterSelectionDefinition,
  FilterSelectionValue,
  FiltersSelection,
} from "~/types";
import { Filters, FiltersType } from "~/types";

export enum SearchMode {
  /**
   * Use query parameters to set/store the filters and execute the search
   * Default search mode
   */
  URL_SEARCH,

  /**
   * Use the local filters only to store and execute the search
   */
  FILTER_SEARCH,
}

export function useSearch(
  filterType: FiltersType,
  appliedFilters?: FiltersSelection,
  searchMode: SearchMode = SearchMode.URL_SEARCH,
) {
  const route = useRoute() ?? {};
  const router = useRouter() ?? {};
  const serialize = useSerialize();

  const filters = reactive<FiltersSelection>(appliedFilters ?? new Map());

  let onSearchCallback: () => void;

  const searchParams = computed(() => getSearchParams());

  let forceSearchCallbackOnRouteChanged = false;

  // watch the route changes and set the filters, then execute the search
  if (searchMode === SearchMode.URL_SEARCH) {
    watch(route, () => {
      if (
        (setFiltersFromQueryParams() || forceSearchCallbackOnRouteChanged) &&
        onSearchCallback
      ) {
        forceSearchCallbackOnRouteChanged = false;

        onSearchCallback();
      }
    });
  }

  // Store the route path at first use
  const routePath = route.path;

  /**
   * Set the filters from query parameter values
   * @return true if the filters have been updated from query parameter values, false otherwise
   */
  function setFiltersFromQueryParams(): boolean {
    let filtersChanged = false;

    // Check if the current route path is the same as the initial one, otherwise this is another page so no need to apply search
    if (route.path === routePath) {
      // Set all the filters from query parameters if they are different
      for (const [filter, filterSelectionDefinition] of filters) {
        const filterValue = toRaw(filterSelectionDefinition.value);
        const isFilterValueArray = Array.isArray(filterValue);
        const param: string = isFilterValueArray ? `${filter}[]` : filter;

        let paramValue;

        if (route.query[param] !== undefined) {
          paramValue = route.query[param];
          paramValue =
            isFilterValueArray && !Array.isArray(paramValue)
              ? [paramValue]
              : paramValue;
        } else {
          paramValue = isFilterValueArray ? [] : "";
        }

        if (serialize(paramValue) !== serialize(filterValue)) {
          setFilter(filter, paramValue as FilterSelectionValue);
          filtersChanged = true;
        }
      }
    }

    return filtersChanged;
  }

  /**
   * Set a filter
   * @param filter
   * @param value
   * @param fallbackValue
   */
  function setFilter(
    filter: Filters,
    value: FilterSelectionValue,
    fallbackValue?: FilterSelectionValue | undefined,
  ): void {
    const filtersSelection: FilterSelectionDefinition | undefined =
      filters.get(filter);

    if (filtersSelection) {
      filtersSelection.value = value;
      filtersSelection.fallbackValue = fallbackValue;
    }
  }

  /**
   * Execute a search with the filters set
   * @param replaceHistory if true, replace the current entry in the history stack
   *                       only available for searchMode = SearchMode.URL_SEARCH
   *                       default = false
   */
  async function search(replaceHistory = false) {
    // In search mode URL_SEARCH => update the route query parameters
    if (searchMode === SearchMode.URL_SEARCH) {
      const query = { ...route.query };

      let filtersChanged = false;

      filters.forEach(
        (
          filterSelectionDefinition: FilterSelectionDefinition,
          filter: Filters,
        ) => {
          if (Array.isArray(filterSelectionDefinition.value)) {
            if (filterSelectionDefinition.value.length === 0) {
              if (query[`${filter}[]`] !== undefined) {
                delete query[`${filter}[]`];
                filtersChanged = true;
              }
            } else {
              const queryFilter = query[`${filter}[]`];
              // compare query parameter value as array to filter value as array
              // the array filter values are stringified because query parameter values are always string
              if (
                serialize(
                  // if the query parameter contains only one value, it is not an array so it is necessary to transform it to array
                  Array.isArray(queryFilter) ? queryFilter : [queryFilter],
                ) !==
                serialize(
                  filterSelectionDefinition.value.map((value) =>
                    value.toString(),
                  ),
                )
              ) {
                query[`${filter}[]`] = filterSelectionDefinition.value;
                filtersChanged = true;
              }
            }
          } else if (filterSelectionDefinition.value === "") {
            if (query[filter] !== undefined) {
              delete query[filter];
              filtersChanged = true;
            }
          } else {
            if (
              serialize(query[filter]) !==
              serialize(filterSelectionDefinition.value)
            ) {
              query[filter] = filterSelectionDefinition.value;
              filtersChanged = true;
            }
          }
        },
      );

      // The filters has changed compared to the route => update the route
      if (filtersChanged) {
        const newRoute = {
          path: route.path,
          query,
        };

        forceSearchCallbackOnRouteChanged = true;

        if (replaceHistory) {
          await router.replace(newRoute);
        } else {
          await router.push(newRoute);
        }
      }
      // The filters has not changed compared to the route query parameters => execute callback
      else if (onSearchCallback) {
        await onSearchCallback();
      }
    }
    // In search mode URL_FILTER => execute callback
    else if (onSearchCallback) {
      await onSearchCallback();
    }
  }

  /**
   * Define the function to call on search execution
   * By default, execute the callback at the same time except if executeCallback = false
   * @param callback function to call on search
   * @param executeCallback true = execute the callback function
   */
  async function onSearch(callback: () => void, executeCallback = true) {
    onSearchCallback = callback;

    // set the filters from query parameters, this is useful when accessing to a page with filters in query parameters
    if (searchMode === SearchMode.URL_SEARCH) {
      setFiltersFromQueryParams();
    }
    if (executeCallback) {
      await search();
    }
  }

  /**
   * Build the query of filters depending on the filter type
   * @returns the formatted query
   */
  function getSearchParams(): string | FiltersSelection {
    if (filterType === FiltersType.RAW) {
      return filters.get(Filters.NAME)?.value as string;
    }

    // get the filters which have a defined value (not empty string or empty array)
    const filtersWithValue: FilterSelectionDefinition[] = [
      ...filters.values(),
    ].filter(
      (filterSelectionDefinition) =>
        filterSelectionDefinition.value.length ||
        filterSelectionDefinition.fallbackValue?.length,
    );

    if (filterType === FiltersType.FILTERS_SELECTION) {
      return initSearchFilterSelection();
    }

    let orFilterFormatter: (formattedFilters: string[]) => string;
    let arrayValueFormatter: (value: string[]) => string;
    let andSeparator: string;

    const mapFormatFilter = (filter: FilterSelectionDefinition) =>
      formatFilter(filter, orFilterFormatter, arrayValueFormatter);

    switch (filterType) {
      case FiltersType.TWO_AMPERSAND_SEPARATOR:
        orFilterFormatter = (formattedFilters: string[]) =>
          `(${formattedFilters.join("||")})`;
        arrayValueFormatter = (values: string[]) => `[${values.join(",")}]`;
        andSeparator = "&&";

        return filtersWithValue.map(mapFormatFilter).join(andSeparator);
      case FiltersType.PIPE_SEPARATOR:
        orFilterFormatter = (formattedFilters: string[]) =>
          `(${formattedFilters.join("|'")})`;
        arrayValueFormatter = (values: string[]) => `[${values.join(",")}]`;
        andSeparator = "|";

        return `(${filtersWithValue.map(mapFormatFilter).join(andSeparator)})`;
      case FiltersType.RSQL:
        orFilterFormatter = (formattedFilters: string[]) =>
          `(${formattedFilters.join(",")})`;
        arrayValueFormatter = (values: string[]) => `(${values.join(",")})`;
        andSeparator = ";";

        return filtersWithValue.map(mapFormatFilter).join(andSeparator);
    }

    return "";
  }

  /**
   * Transform a FilterSelectionDefinition to a formatted string query
   * @param filterSelectionDefinition
   * @param orFilterFormatter
   * @param arrayValueFormatter
   * @returns query as string
   */
  function formatFilter(
    filterSelectionDefinition: FilterSelectionDefinition,
    orFilterFormatter: (formattedFilters: string[]) => string,
    arrayValueFormatter: (value: string[]) => string,
  ): string {
    // case when the key is an array => it is considered to be an OR query => format the filter for each key recursively
    if (Array.isArray(filterSelectionDefinition.key)) {
      const formattedValues = filterSelectionDefinition.key.map((key) =>
        formatFilter(
          { ...filterSelectionDefinition, key },
          orFilterFormatter,
          arrayValueFormatter,
        ),
      );
      return orFilterFormatter(formattedValues);
    } else {
      let value =
        filterSelectionDefinition.value ??
        filterSelectionDefinition.fallbackValue ??
        "";

      if (filterSelectionDefinition.valueFormatter) {
        value = filterSelectionDefinition.valueFormatter(value);
      }

      if (Array.isArray(value)) {
        value = arrayValueFormatter(value);
      }

      return `${filterSelectionDefinition.key}${filterSelectionDefinition.operator}${value}`;
    }
  }

  function initSearchFilterSelection(): FiltersSelection {
    const searchFilterSelection: FiltersSelection = new Map<
      Filters,
      FilterSelectionDefinition
    >();
    const searchKeys = Array.from(filters.keys()).filter(
      (filterName: Filters) => {
        const definition = filters.get(filterName);
        return !!definition?.value?.length;
      },
    );
    for (const filterName of searchKeys) {
      searchFilterSelection.set(
        filterName,
        filters.get(filterName) as FilterSelectionDefinition,
      );
    }
    return searchFilterSelection;
  }

  return {
    searchParams,
    filters,
    setFilter,
    onSearch,
    search,
  };
}
